import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, X, Zap, ListOrdered, Save, Loader2 } from 'lucide-react';

export const FlowFormDialog = ({
  open,
  onOpenChange,
  editingFlow,
  formData,
  setFormData,
  tasks,
  keywordInput,
  setKeywordInput,
  phraseInput,
  setPhraseInput,
  patternInput,
  setPatternInput,
  formLoading,
  onSubmit,
  onAddKeyword,
  onRemoveKeyword,
  onAddPhrase,
  onRemovePhrase,
  onAddPattern,
  onRemovePattern,
  onToggleTask
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingFlow ? 'Editar Flujo' : 'Nuevo Flujo'}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Nombre *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Ej: Flujo de Bienvenida"
              />
            </div>
            <div className="space-y-2">
              <Label>Prioridad (0-100)</Label>
              <Input
                type="number"
                min="0"
                max="100"
                value={formData.priority}
                onChange={(e) => setFormData({...formData, priority: parseInt(e.target.value) || 0})}
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>Descripción</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Descripción del flujo..."
              rows={2}
            />
          </div>
          
          <Separator />
          
          {/* Triggers */}
          <div className="space-y-4">
            <h3 className="font-medium flex items-center gap-2">
              <Zap className="h-4 w-4" />
              Disparadores (Triggers)
            </h3>
            
            {/* Keywords */}
            <div className="space-y-2">
              <Label>Palabras Clave</Label>
              <div className="flex gap-2">
                <Input
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), onAddKeyword())}
                  placeholder="Agregar palabra clave..."
                />
                <Button type="button" variant="outline" onClick={onAddKeyword}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.trigger_keywords.map(kw => (
                  <Badge key={kw} variant="secondary" className="gap-1">
                    {kw}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => onRemoveKeyword(kw)} />
                  </Badge>
                ))}
              </div>
            </div>
            
            {/* Phrases */}
            <div className="space-y-2">
              <Label>Frases Exactas</Label>
              <div className="flex gap-2">
                <Input
                  value={phraseInput}
                  onChange={(e) => setPhraseInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), onAddPhrase())}
                  placeholder="Agregar frase exacta..."
                />
                <Button type="button" variant="outline" onClick={onAddPhrase}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.trigger_phrases.map(ph => (
                  <Badge key={ph} variant="outline" className="gap-1">
                    "{ph}"
                    <X className="h-3 w-3 cursor-pointer" onClick={() => onRemovePhrase(ph)} />
                  </Badge>
                ))}
              </div>
            </div>
            
            {/* Patterns */}
            <div className="space-y-2">
              <Label>Patrones Regex</Label>
              <div className="flex gap-2">
                <Input
                  value={patternInput}
                  onChange={(e) => setPatternInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), onAddPattern())}
                  placeholder="Agregar patrón regex..."
                />
                <Button type="button" variant="outline" onClick={onAddPattern}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.trigger_patterns.map(pt => (
                  <Badge key={pt} variant="destructive" className="gap-1 font-mono text-xs">
                    {pt}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => onRemovePattern(pt)} />
                  </Badge>
                ))}
              </div>
            </div>
          </div>
          
          <Separator />
          
          {/* Tasks Selection */}
          <div className="space-y-4">
            <h3 className="font-medium flex items-center gap-2">
              <ListOrdered className="h-4 w-4" />
              Tareas del Flujo
            </h3>
            <ScrollArea className="h-48 border rounded-lg p-2">
              {tasks.length > 0 ? (
                <div className="space-y-2">
                  {tasks.map(task => (
                    <div
                      key={task.id}
                      className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                        formData.task_ids.includes(task.id) ? 'bg-[#FDE8EA] border border-[#E63946]' : 'hover:bg-gray-50'
                      }`}
                      onClick={() => onToggleTask(task.id)}
                    >
                      <input
                        type="checkbox"
                        checked={formData.task_ids.includes(task.id)}
                        onChange={() => onToggleTask(task.id)}
                        className="rounded"
                      />
                      <div className="flex-1">
                        <p className="font-medium text-sm">{task.name}</p>
                        <p className="text-xs text-gray-500">
                          {task.closure_mode === 'system' ? '⚙️ Auto' : '👤 Manual'} | Orden: {task.order}
                        </p>
                      </div>
                      {formData.task_ids.includes(task.id) && (
                        <Badge className="bg-[#E63946]">
                          #{formData.task_ids.indexOf(task.id) + 1}
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-4">No hay tareas disponibles</p>
              )}
            </ScrollArea>
          </div>
          
          <Separator />
          
          {/* Options */}
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2">
              <Switch
                checked={formData.can_be_triggered}
                onCheckedChange={(v) => setFormData({...formData, can_be_triggered: v})}
              />
              <span className="text-sm">Puede ser disparado</span>
            </label>
            <label className="flex items-center gap-2">
              <Switch
                checked={formData.is_fallback}
                onCheckedChange={(v) => setFormData({...formData, is_fallback: v})}
              />
              <span className="text-sm">Flujo de fallback</span>
            </label>
            <Select
              value={formData.status}
              onValueChange={(v) => setFormData({...formData, status: v})}
            >
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Activo</SelectItem>
                <SelectItem value="inactive">Inactivo</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={formLoading}>
            Cancelar
          </Button>
          <Button onClick={onSubmit} disabled={formLoading}>
            {formLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            {editingFlow ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FlowFormDialog;
